import { createSelector, MemoizedSelector, select } from '@ngrx/store';

import { safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { BoundType } from '@fcom/dapi/interfaces/itinerary.interface';
import { PaxAmount } from '@fcom/dapi/interfaces/pax-details.interface';
import { isPresent } from '@fcom/core/utils/utils';
import { mergeDeep } from '@fcom/core/utils/merge';
import { Cabin } from '@fcom/dapi/api/models/cabin';
import { FareFamily } from '@fcom/dapi/api/models/fare-family';
import { FinnairAirBoundsRequest } from '@fcom/dapi/api/models/finnair-air-bounds-request';
import { FinnairBoundFareFamily } from '@fcom/dapi/api/models/finnair-bound-fare-family';
import { FinnairCabinClass } from '@fcom/dapi/api/models/finnair-cabin-class';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { FareInformation } from '@fcom/dapi/api/models/fare-information';
import { SortBy } from '@fcom/common-booking';
import { shouldTimeBeKept } from '@fcom/common-booking/utils/date-utils';
import { FlightFilters, FlightTimeOption } from '@fcom/common-booking/interfaces/flight-filters.interface';
import { isFlight } from '@fcom/common-booking/utils/flight.utils';

import { GtmFlightSelectionData, GtmPurchaseFlow } from '../../interfaces/gtm.interface';
import { OfferListFetchParams } from '../../interfaces/offer-list-fetch-params';
import { getGtmFlightSelectionDataForFareFamily } from '../../utils/gtm.utils';
import { isShortHaulFlight } from '../../utils/flight.utils';
import { FLIGHT_SELECTION_CACHE_EXPIRY } from '../../config/booking-config';
import { FinnairAirBounds, FinnairBoundGroupWithLocation } from '../../interfaces/booking/offers.interface';
import { bookingFeatureState } from '../../store/selectors/booking-feature-state.selector';
import { TicketSelectionParams } from '../../interfaces/booking/booking-air-bounds.interface';
import { AirBoundsState } from '../../interfaces/booking/air-bounds-state.interface';
import { AirOffersStatus } from '../../interfaces/booking/air-offers-state.interface';
import { BookingAppState, BookingFeatureState } from '../../interfaces/booking/booking-state.interface';
import { FareFamilyMap } from '../../interfaces/booking/booking-offer-list.interface';

const _airBoundsState = createSelector(bookingFeatureState, (_bfs: BookingFeatureState) => _bfs?.bounds);
export const boundsStatusSelector = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.status);
const _boundRefundale = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.filters?.refundable);
const _boundDepartureTime = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.filters?.departureTime);
const _boundArrivalTime = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.filters?.arrivalTime);
const _boundMaxStops = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.filters?.maxStops);
const _boundCabin = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.filters?.cabin);
export const selectedOutboundFareFamilySelector = createSelector(
  _airBoundsState,
  (_abs: AirBoundsState) => _abs?.selections?.outbound?.fareFamily
);
export const selectedInboundFareFamilySelector = createSelector(
  _airBoundsState,
  (_abs: AirBoundsState) => _abs?.selections?.inbound?.fareFamily
);

const _boundRefundableNext = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.nextFilters?.refundable);
const _boundDepartureTimeNext = createSelector(
  _airBoundsState,
  (_abs: AirBoundsState) => _abs?.nextFilters?.departureTime
);
const _boundArrivalTimeNext = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.nextFilters?.arrivalTime);
const _boundCabinNext = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.nextFilters?.cabin);
const _boundMaxStopsNext = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.nextFilters?.maxStops);

export const outboundsSelector = createSelector(
  _airBoundsState,
  (airBoundsState: AirBoundsState) => airBoundsState?.outbounds
);
export const inboundsSelector = createSelector(
  _airBoundsState,
  (airBoundsState: AirBoundsState) => airBoundsState?.inbounds
);
export const upsellBoundsSelector = createSelector(
  _airBoundsState,
  (airBoundsState: AirBoundsState) => airBoundsState?.upsellBounds
);
/**
 * TODO: It would be safer to use the inbound hash (if existing) because it has newer timestamp than outbound.
 */
const _boundsHash = createSelector(outboundsSelector, (_b: FinnairAirBounds) => _b?.hash);
export const boundsCurrencySelector = createSelector(outboundsSelector, (_b: FinnairAirBounds) => _b?.currency);
const _request = createSelector(outboundsSelector, (_b: FinnairAirBounds) => _b?.request);
const _inboundsFareFamiliesSorting = createSelector(
  inboundsSelector,
  (_b: FinnairAirBounds) => _b?.sorting?.fareFamilies
);
const _outboundsFareFamiliesSorting = createSelector(
  outboundsSelector,
  (_b: FinnairAirBounds) => _b?.sorting?.fareFamilies
);

const _outboundsSelectedCheapestOffer = createSelector(
  _airBoundsState,
  (_b: AirBoundsState) => _b?.isSelectedCheapestOffer
);

/**
 * Selects whether the bounds query was for one way
 */
export const isOneWayBoundsSelector = createSelector(_request, (_r) => (_r ? _r.itineraries.length === 1 : undefined));
export const lastRequestParamsSelector = createSelector(
  _airBoundsState,
  (_abs: AirBoundsState) => _abs?.lastRequestParams
);
const _serializedLastRequestParams = createSelector(lastRequestParamsSelector, (lrs: OfferListFetchParams) =>
  JSON.stringify(lrs)
);
export const airBoundsLastRequestDepartureDateSelector = createSelector(
  lastRequestParamsSelector,
  (lrs: OfferListFetchParams) => lrs?.flights?.[0]?.departureDate
);
export const airBoundsLastRequestReturnDateSelector = createSelector(
  lastRequestParamsSelector,
  (lrs: OfferListFetchParams) => lrs?.flights?.[1]?.departureDate
);
const _outboundsBoundGroupsSelector = createSelector(outboundsSelector, (_obs: FinnairAirBounds) => _obs?.boundGroups);
const _inboundsBoundGroupsSelector = createSelector(inboundsSelector, (_obs: FinnairAirBounds) => _obs?.boundGroups);
export const selectedOutboundAirBoundIdSelector = createSelector(
  _airBoundsState,
  (_abs: AirBoundsState) => _abs?.selectedOutboundAirBoundId
);
export const selectedInboundAirBoundIdSelector = createSelector(
  _airBoundsState,
  (_abs: AirBoundsState) => _abs?.selectedInboundAirBoundId
);
export const selectedUpsellBoundAirBoundIdSelector = createSelector(
  _airBoundsState,
  (_abs: AirBoundsState) => _abs?.selectedUpsellBoundAirBoundId
);
const _upsellBoundsBoundGroupsSelector = createSelector(
  upsellBoundsSelector,
  (_obs: FinnairAirBounds) => _obs?.boundGroups
);
export const selectedAirBoundIdsSelector = createSelector(
  isOneWayBoundsSelector,
  selectedOutboundAirBoundIdSelector,
  selectedInboundAirBoundIdSelector,
  (oneway: boolean, outboundAirBoundId: string, inboundAirBoundId: string): string[] => {
    if (oneway) {
      return [outboundAirBoundId];
    }
    return [outboundAirBoundId, inboundAirBoundId];
  }
);
export const selectedOutboundBoundSelector = createSelector(
  _outboundsBoundGroupsSelector,
  selectedOutboundAirBoundIdSelector,
  (
    _outboundBoundGroups: FinnairBoundGroupWithLocation[] = [],
    selectedOutBoundFareFamilyId: string
  ): FinnairBoundGroupWithLocation | undefined =>
    _outboundBoundGroups.find((b: FinnairBoundGroupWithLocation) =>
      b.fareFamilies.some((ff) => ff.id === selectedOutBoundFareFamilyId)
    )
);
export const selectedInboundBoundSelector = createSelector(
  _inboundsBoundGroupsSelector,
  selectedInboundAirBoundIdSelector,
  (
    inboundBoundGroups: FinnairBoundGroupWithLocation[] = [],
    selectedInBoundFareFamilyId: string
  ): FinnairBoundGroupWithLocation | undefined =>
    inboundBoundGroups.find((b: FinnairBoundGroupWithLocation) =>
      b.fareFamilies.some((ff) => ff.id === selectedInBoundFareFamilyId)
    )
);
export const selectedUpsellBoundSelector = createSelector(
  _upsellBoundsBoundGroupsSelector,
  selectedUpsellBoundAirBoundIdSelector,
  (
    upsellBoundGroups: FinnairBoundGroupWithLocation[] = [],
    selectedUpsellFareFamilyId: string
  ): FinnairBoundGroupWithLocation | undefined =>
    upsellBoundGroups.find((b: FinnairBoundGroupWithLocation) =>
      b.fareFamilies.some((ff) => ff.id === selectedUpsellFareFamilyId)
    )
);
export const selectedOutboundAirBoundSelector = createSelector(
  selectedOutboundBoundSelector,
  selectedOutboundAirBoundIdSelector,
  (_b: FinnairBoundGroupWithLocation, _ffId: string): FinnairBoundFareFamily =>
    isPresent(_b) ? _b.fareFamilies.find((ff) => ff.id === _ffId) : undefined
);
export const selectedInboundAirBoundSelector = createSelector(
  selectedInboundBoundSelector,
  selectedInboundAirBoundIdSelector,
  (_b: FinnairBoundGroupWithLocation, _ffId: string): FinnairBoundFareFamily =>
    isPresent(_b) ? _b.fareFamilies.find((ff) => ff.id === _ffId) : undefined
);
const _selectedOutboundFareFamilyCodeBoundsSelector = createSelector(
  selectedOutboundAirBoundSelector,
  (_ff) => _ff?.fareFamilyCode
);
const _selectedInboundFareFamilyCodeBoundsSelector = createSelector(
  selectedInboundAirBoundSelector,
  (_ff) => _ff?.fareFamilyCode
);
export const selectedOutboundIdBoundsSelector = createSelector(
  selectedOutboundBoundSelector,
  (_b: FinnairBoundGroupWithLocation) => _b?.sortingId
);
export const selectedInboundIdBoundsSelector = createSelector(
  selectedInboundBoundSelector,
  (_b: FinnairBoundGroupWithLocation) => _b?.sortingId
);
const _outboundFareFamilies = createSelector(outboundsSelector, (_b: FinnairAirBounds) => _b?.fareFamilies);
const _inboundFareFamilies = createSelector(inboundsSelector, (_b: FinnairAirBounds) => _b?.fareFamilies);
export const selectedOutboundFareFamilyBenefitsSelector = createSelector(
  _outboundFareFamilies,
  _selectedOutboundFareFamilyCodeBoundsSelector,
  (_ffMap: FareFamilyMap, _ffCode: string): FareFamily => _ffMap?.[_ffCode]
);
export const selectedInboundFareFamilyBenefitsSelector = createSelector(
  _inboundFareFamilies,
  _selectedInboundFareFamilyCodeBoundsSelector,
  (_ffMap: FareFamilyMap, _ffCode: string): FareFamily => _ffMap?.[_ffCode]
);
const mapItinerary = (fareFamilyCode: string, bound: FinnairBoundGroupWithLocation | undefined) => {
  if (!bound) {
    return [];
  }

  const fareInformation = bound.fareFamilies.find((f) => f.fareFamilyCode === fareFamilyCode)?.fareInformation;

  return bound.details.itinerary.filter(isFlight).map((flight, index) => ({
    ...flight,
    bookingClass: fareInformation?.[index]?.bookingClass,
    cabinClass: fareInformation?.[index]?.cabinClass as FinnairCabinClass,
  }));
};
export const selectedOutboundItinerarySelector = createSelector(
  _selectedOutboundFareFamilyCodeBoundsSelector,
  selectedOutboundBoundSelector,
  (_ffCode, bound): FinnairItineraryItemFlight[] => mapItinerary(_ffCode, bound)
);
export const selectedInboundItinerarySelector = createSelector(
  _selectedInboundFareFamilyCodeBoundsSelector,
  selectedInboundBoundSelector,
  (_ffCode, bound): FinnairItineraryItemFlight[] => mapItinerary(_ffCode, bound)
);
export const fareFamiliesBoundsSelector = createSelector(
  _outboundFareFamilies,
  _inboundFareFamilies,
  (outboundFFs, inboundFFs) => mergeDeep(outboundFFs, inboundFFs)
);
export const selectedAirBoundsSortBySelector = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.sortBy);

const _flightSelectionGtmDataForAirBound = (
  bound: FinnairBoundGroupWithLocation,
  boundType: BoundType,
  paxAmount: PaxAmount
) =>
  createSelector(
    boundsCurrencySelector,
    fareFamiliesBoundsSelector,
    lastRequestParamsSelector,
    (currencyCode: string, fareFamilyBenefitsMap: FareFamilyMap, lrs): GtmFlightSelectionData[] =>
      isPresent(currencyCode) && isPresent(fareFamilyBenefitsMap) && isPresent(paxAmount)
        ? bound.fareFamilies.map((fareFamily) =>
            getGtmFlightSelectionDataForFareFamily(
              bound.details,
              boundType,
              bound.details.itinerary,
              fareFamilyBenefitsMap[fareFamily.fareFamilyCode]?.brandName,
              fareFamily.price,
              undefined,
              fareFamily.fareInformation,
              currencyCode,
              paxAmount,
              lrs.isAward ? GtmPurchaseFlow.AWARD : GtmPurchaseFlow.BOOKING
            )
          )
        : undefined
  );

const _boundSelectionGtmDataSelectorGenerator = (
  boundType: BoundType,
  boundSelector: MemoizedSelector<BookingAppState, FinnairBoundGroupWithLocation>,
  fareFamilySelector: MemoizedSelector<BookingAppState, FinnairBoundFareFamily>,
  paxAmount: PaxAmount
) =>
  createSelector(
    boundSelector,
    fareFamilySelector,
    boundsCurrencySelector,
    fareFamiliesBoundsSelector,
    lastRequestParamsSelector,
    (
      bound: FinnairBoundGroupWithLocation,
      fareFamily: FinnairBoundFareFamily,
      currencyCode: string,
      fareFamilyBenefitsMap: FareFamilyMap,
      lrs
    ): GtmFlightSelectionData => {
      return isPresent(bound) &&
        isPresent(fareFamily) &&
        isPresent(currencyCode) &&
        isPresent(fareFamilyBenefitsMap) &&
        isPresent(paxAmount)
        ? getGtmFlightSelectionDataForFareFamily(
            bound.details,
            boundType,
            bound.details.itinerary,
            fareFamilyBenefitsMap[fareFamily.fareFamilyCode]?.brandName,
            fareFamily.price,
            undefined,
            fareFamily.fareInformation,
            currencyCode,
            paxAmount,
            lrs.isAward ? GtmPurchaseFlow.AWARD : GtmPurchaseFlow.BOOKING
          )
        : undefined;
    }
  );

/**
 * Selects true or false dependending whether the selected ticket types have no baggage.
 * Handles both one way and return travel types.
 * @param {BookingOffer} offer
 */
const _hasZeroBaggageOnEitherBound = createSelector(
  fareFamiliesBoundsSelector,
  isOneWayBoundsSelector,
  _selectedOutboundFareFamilyCodeBoundsSelector,
  _selectedInboundFareFamilyCodeBoundsSelector,
  (fareFamilies: FareFamilyMap, isOneWay: boolean, outboundFareFamilyCode: string, inboundFareFamilyCode: string) => {
    if (!fareFamilies) {
      return undefined;
    }
    const outboundFareFamily: FareFamily = fareFamilies[outboundFareFamilyCode];
    const outboundBaggage = outboundFareFamily?.checkedBaggage || 0;
    if (isOneWay) {
      return outboundBaggage === 0;
    }
    const inboundFareFamily: FareFamily = fareFamilies[inboundFareFamilyCode];
    const inboundBaggage = inboundFareFamily?.checkedBaggage || 0;
    return outboundBaggage === 0 || inboundBaggage === 0;
  }
);

/**
 * Selects fare families brands.
 * Handles both one way and return travel types.
 */
export const selectedFareFamiliesBrandsSelector = createSelector(
  fareFamiliesBoundsSelector,
  isOneWayBoundsSelector,
  _selectedOutboundFareFamilyCodeBoundsSelector,
  _selectedInboundFareFamilyCodeBoundsSelector,
  (fareFamilies: FareFamilyMap, isOneWay: boolean, outboundFareFamilyCode: string, inboundFareFamilyCode: string) => {
    if (!fareFamilies) {
      return undefined;
    }
    const outboundFareFamily: FareFamily = fareFamilies[outboundFareFamilyCode];
    if (isOneWay) {
      return [outboundFareFamily?.brandName];
    }
    const inboundFareFamily: FareFamily = fareFamilies[inboundFareFamilyCode];
    return [outboundFareFamily?.brandName, inboundFareFamily?.brandName];
  }
);

export const isShortHaulOutboundsSelector = createSelector(outboundsSelector, (bounds: FinnairAirBounds) =>
  bounds?.request.itineraries.every((itinerary) =>
    isShortHaulFlight(
      bounds.locations[itinerary.departureLocationCode],
      bounds.locations[itinerary.destinationLocationCode]
    )
  )
);

export const boundsTotalPriceSelector = createSelector(
  selectedOutboundAirBoundSelector,
  selectedInboundAirBoundSelector,
  boundsCurrencySelector,
  (outboundFareFamily, inboundFareFamily, currencyCode): Amount => {
    const totalSum = inboundFareFamily?.totalPrice || outboundFareFamily?.totalPrice;
    const amount = totalSum === '0' ? undefined : totalSum;

    return { amount, currencyCode };
  }
);

export const filteredOutboundsSelector = createSelector(
  outboundsSelector,
  fareFamiliesBoundsSelector,
  _boundRefundale,
  _boundDepartureTime,
  _boundArrivalTime,
  _boundCabin,
  _boundMaxStops,
  filterBounds
);

export const filteredInboundsSelector = createSelector(
  inboundsSelector,
  fareFamiliesBoundsSelector,
  _boundRefundale,
  _boundDepartureTime,
  _boundArrivalTime,
  _boundCabin,
  _boundMaxStops,
  filterBounds
);

const _nextFilteredOutbounds = createSelector(
  outboundsSelector,
  fareFamiliesBoundsSelector,
  _boundRefundableNext,
  _boundDepartureTimeNext,
  _boundArrivalTimeNext,
  _boundCabinNext,
  _boundMaxStopsNext,
  filterBounds
);

const _nextFilteredInbounds = createSelector(
  inboundsSelector,
  fareFamiliesBoundsSelector,
  _boundRefundableNext,
  _boundDepartureTimeNext,
  _boundArrivalTimeNext,
  _boundCabinNext,
  _boundMaxStopsNext,
  filterBounds
);

export const inboundsFareFamiliesSortingSelector = (): StateSelector<BookingFeatureState, string[]> =>
  select(_inboundsFareFamiliesSorting);

export const outboundsFareFamiliesSortingSelector = (): StateSelector<BookingFeatureState, string[]> =>
  select(_outboundsFareFamiliesSorting);

/**
 * Selects the current status of the air bounds query
 */
export const boundsStatus = (): StateSelector<BookingFeatureState, AirOffersStatus> => safeSelect(boundsStatusSelector);

export const boundsRefundable = (): StateSelector<BookingFeatureState, boolean> => safeSelect(_boundRefundale);

export const boundsDepartureTime = (): StateSelector<BookingFeatureState, FlightTimeOption[]> =>
  safeSelect(_boundDepartureTime);

export const boundsReturnTime = (): StateSelector<BookingFeatureState, FlightTimeOption[]> =>
  safeSelect(_boundArrivalTime);

/**
 * Selects the outbounds from the state. Will not emit if not present.
 */
export const outbounds = (): StateSelector<BookingFeatureState, FinnairAirBounds> => select(outboundsSelector);

/**
 * Selects the inbounds from the state. Will not emit if not present.
 */
export const inbounds = (): StateSelector<BookingFeatureState, FinnairAirBounds> => select(inboundsSelector);

/**
 * Selects the upsell bounds (ECLASIC1 parallel request response) from the state.
 */
export const upsellBounds = (): StateSelector<BookingFeatureState, FinnairAirBounds> => select(upsellBoundsSelector);

/**
 * Selects the filtered (not applied) outbounds if present
 */
export const nextFilteredOutbounds = (): StateSelector<BookingFeatureState, FinnairAirBounds> =>
  select(_nextFilteredOutbounds);

/**
 * Selects the filtered (not applied) inbounds if present
 */
export const nextFilteredInbounds = (): StateSelector<BookingFeatureState, FinnairAirBounds> =>
  select(_nextFilteredInbounds);

/**
 * Gets the hash returned with the bounds
 */
export const boundsHash = (): StateSelector<BookingFeatureState, string> => safeSelect(_boundsHash);

/**
 *  Selects all possible fare families
 */
export const fareFamiliesBounds = (): StateSelector<BookingFeatureState, FinnairAirBounds['fareFamilies']> =>
  select(fareFamiliesBoundsSelector);

/**
 * Params of the last request
 */
export const lastRequestParams = (): StateSelector<BookingFeatureState, TicketSelectionParams> =>
  select(lastRequestParamsSelector);

/**
 * Serialized version of the params of the last request
 */
export const serializedLastRequestParams = (): StateSelector<BookingFeatureState, string> =>
  select(_serializedLastRequestParams);

export const selectedOutboundIdBounds = (): StateSelector<BookingFeatureState, string> =>
  select(selectedOutboundIdBoundsSelector);
export const selectedOutboundFareFamilyCodeBounds = (): StateSelector<BookingFeatureState, string> =>
  select(_selectedOutboundFareFamilyCodeBoundsSelector);
export const selectedInboundIdBounds = (): StateSelector<BookingFeatureState, string> =>
  select(selectedInboundIdBoundsSelector);
export const selectedInboundFareFamilyCodeBounds = (): StateSelector<BookingFeatureState, string> =>
  select(_selectedInboundFareFamilyCodeBoundsSelector);
export const selectedOutboundAirBoundId = (): StateSelector<BookingFeatureState, string> =>
  select(selectedOutboundAirBoundIdSelector);
export const selectedInboundAirBoundId = (): StateSelector<BookingFeatureState, string> =>
  select(selectedInboundAirBoundIdSelector);
export const selectedOutboundAirBound = (): StateSelector<BookingFeatureState, FinnairBoundFareFamily> =>
  select(selectedOutboundAirBoundSelector);
export const selectedInboundAirBound = (): StateSelector<BookingFeatureState, FinnairBoundFareFamily> =>
  select(selectedInboundAirBoundSelector);
export const selectedOutboundFareFamilyBenefits = (): StateSelector<BookingFeatureState, FareFamily> =>
  select(selectedOutboundFareFamilyBenefitsSelector);
export const selectedInboundFareFamilyBenefits = (): StateSelector<BookingFeatureState, FareFamily> =>
  select(selectedInboundFareFamilyBenefitsSelector);
export const selectedOutboundItinerary = (): StateSelector<BookingFeatureState, FinnairItineraryItemFlight[]> =>
  select(selectedOutboundItinerarySelector);
export const selectedInboundItinerary = (): StateSelector<BookingFeatureState, FinnairItineraryItemFlight[]> =>
  select(selectedInboundItinerarySelector);
export const airBoundsRequest = (): StateSelector<BookingFeatureState, FinnairAirBoundsRequest> => select(_request);

export const selectedAirBoundsTicketSortBy = (): StateSelector<BookingFeatureState, SortBy> =>
  select(selectedAirBoundsSortBySelector);

export const hasZeroBaggageOnEitherBound = (): StateSelector<BookingFeatureState, boolean> =>
  safeSelect(_hasZeroBaggageOnEitherBound);

// Analytics related selectors
export const outboundSelectionGtmData = (
  paxAmount: PaxAmount
): StateSelector<BookingFeatureState, GtmFlightSelectionData> =>
  safeSelect(
    _boundSelectionGtmDataSelectorGenerator(
      BoundType.outbound,
      selectedOutboundBoundSelector,
      selectedOutboundAirBoundSelector,
      paxAmount
    )
  );
export const inboundSelectionGtmData = (
  paxAmount: PaxAmount
): StateSelector<BookingFeatureState, GtmFlightSelectionData> =>
  safeSelect(
    _boundSelectionGtmDataSelectorGenerator(
      BoundType.inbound,
      selectedInboundBoundSelector,
      selectedInboundAirBoundSelector,
      paxAmount
    )
  );
export const flightSelectionGtmDataForAirBound = (
  bound: FinnairBoundGroupWithLocation,
  boundType: BoundType,
  paxAmount: PaxAmount
): StateSelector<BookingFeatureState, GtmFlightSelectionData[]> =>
  safeSelect(_flightSelectionGtmDataForAirBound(bound, boundType, paxAmount));

export const selectedCheapestOffer = (): StateSelector<BookingAppState, boolean> =>
  select(_outboundsSelectedCheapestOffer);

export const selectFareFamiliesBrands = (): StateSelector<BookingFeatureState, string[]> =>
  select(selectedFareFamiliesBrandsSelector);

function filterBounds(
  bound: FinnairAirBounds,
  fareFamilies: FareFamilyMap,
  refundable: boolean,
  departureTimes: FlightTimeOption[],
  arrivalTimes: FlightTimeOption[],
  cabin: Cabin,
  maxStops: number
): FinnairAirBounds {
  let filteredItems = bound;
  if (!filteredItems) {
    return filteredItems;
  }

  if (departureTimes && departureTimes.length !== 3) {
    filteredItems = {
      ...filteredItems,
      boundGroups: filteredItems.boundGroups?.filter((b) => {
        const dateTime = b.details.departure.dateTime;

        return shouldTimeBeKept(departureTimes, dateTime);
      }),
    };
  }

  if (arrivalTimes && arrivalTimes.length !== 3) {
    filteredItems = {
      ...filteredItems,
      boundGroups: filteredItems.boundGroups?.filter((b) => {
        const dateTime = b.details.arrival.dateTime;

        return shouldTimeBeKept(arrivalTimes, dateTime);
      }),
    };
  }

  if (refundable) {
    filteredItems = {
      ...filteredItems,
      boundGroups: filteredItems.boundGroups?.map((b1) => ({
        ...b1,
        fareFamilies: b1.fareFamilies.filter(
          (ff) => fareFamilies[ff.fareFamilyCode].translatedBenefitRows.find((b2) => b2.key === 'cancel')?.isPositive
        ),
      })),
    };
  }

  if (maxStops != null) {
    filteredItems = {
      ...filteredItems,
      boundGroups: filteredItems.boundGroups?.filter((boundGroup) =>
        maxStops === 0 ? boundGroup.details.stops === 0 : maxStops >= boundGroup.details.stops
      ),
    };
  }

  if (cabin && cabin !== Cabin.MIXED) {
    filteredItems = {
      ...filteredItems,
      boundGroups: filteredItems.boundGroups
        ?.map((b) => ({
          ...b,
          fareFamilies: b.fareFamilies.filter((fareFamily) => {
            const fareInformation = fareFamily.fareInformation.filter(
              ({ cabinClass }: FareInformation) => cabinClass === cabin
            );

            return fareInformation.length > 0;
          }),
        }))
        .filter((boundGroup) => boundGroup.fareFamilies.length > 0),
    };
  }

  return filteredItems;
}

const _filters = createSelector(_airBoundsState, (_abs: AirBoundsState) => _abs?.filters);
export const filters = (): StateSelector<BookingFeatureState, FlightFilters> => select(_filters);

const _outboundCache = (hash: string, currentTime: number) => {
  return createSelector(_airBoundsState, (_abs: AirBoundsState) => {
    const resultInCache = _abs?.outboundCache[hash];

    // Cache expires after 15 minutes
    if (resultInCache?.createdAt + FLIGHT_SELECTION_CACHE_EXPIRY > currentTime) {
      return resultInCache.results;
    }

    return null;
  });
};

export const outboundCache = (
  hash: string,
  currentTime: number
): StateSelector<BookingFeatureState, FinnairAirBounds> => select(_outboundCache(hash, currentTime));
